// /certificates — device PKI 檢視 (device-CA appliance, via the restful-api
// admin proxy /v3/admin/pki/*). **唯讀**:
//   - 查詢單台憑證 metadata（無私鑰）。
//   - 取 CA chain（公開 PEM，下載安裝信任錨用）。
//   - 列裝置/憑證清單（status 篩選）。
//
// ⚠️ **簽發已於 2026-08-02 退場**(restful-api#140,業主裁決)。後端
// `POST /v3/admin/pki/devices/{did}/cert` 已移除(404)。
//
// 依據是 device-ca 2026-06-27 的 **DID 方案變更**:DID 改由 device-ca 自己產生
// (atomic counter + FPE),唯一建立路徑是批量 —— 「對一個外部指定的 DID 當場簽一張」
// 在新模型下**沒有對應的原語**,而不是被政策禁止。現行入口＝device-ca 的批量首簽與
// RMA 重簽,與 ADR-019 的封閉簽發機同向。
//
// 這推翻了 D31① 的「簽發維持保留」,走的是 D31① 自己寫的出口(「僅當 device-ca 已具
// RMA 批次簽發路徑才改選退場」,該路徑現已在位)。**不要把簽發 UI 加回來** —— 沒有
// 後端端點可打,而要有端點需先做 ADR-019/021/022 級的架構變更。
//
// SECURITY: 私鑰永不進瀏覽器；本頁只顯示公開 metadata。device-CA 未配置時(404/503)
// 顯示「未設定」誠實態。

import { useCallback, useState } from 'react';
import { ApiError } from '../../lib/api';
import { useAuth } from '../../auth/AuthContext';
import { formatTimestampUs } from '../../lib/format';
import { ComingSoon } from '../../components/ComingSoon';
import { EmptyState, ErrorState, LoadingState } from '../../components/StateBlock';
import { getCAChain, listDevices, lookupCert } from './pkiApi';
import type { CertRecord, PkiDevice } from './pkiApi';
import './certificates.css';

/** device-ca 的 not_before/not_after 是 RFC3339 字串,不是微秒 epoch。 */
function formatRfc3339(v: string | null | undefined): string {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? v : d.toLocaleString();
}

function isNotConfigured(e: unknown): boolean {
  return e instanceof ApiError && (e.status === 404 || e.status === 503);
}

function download(name: string, text: string, type = 'application/x-pem-file') {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

export default function CertificatesPage() {
  useAuth(); // 讀面對整個 admin 集合開放,無角色分支
  const [did, setDid] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [result, setResult] = useState<CertRecord | null>(null);
  const [notConfigured, setNotConfigured] = useState(false);

  const run = useCallback(
    async (fn: () => Promise<CertRecord>) => {
      const id = did.trim();
      if (!id) {
        setError(new Error('請先輸入裝置 ID'));
        return;
      }
      setBusy(true);
      setError(null);
      setResult(null);
      try {
        setResult(await fn());
      } catch (e) {
        if (isNotConfigured(e)) setNotConfigured(true);
        else setError(e);
      } finally {
        setBusy(false);
      }
    },
    [did],
  );

  const onLookup = () => void run(() => lookupCert(did.trim()));

  // ---- CA chain ----
  const [chain, setChain] = useState<string | null>(null);
  const [chainErr, setChainErr] = useState<unknown>(null);
  const loadChain = async () => {
    setChainErr(null);
    try {
      setChain(await getCAChain());
    } catch (e) {
      if (isNotConfigured(e)) setNotConfigured(true);
      else setChainErr(e);
    }
  };

  // ---- device list ----
  const [statusFilter, setStatusFilter] = useState('');
  const [devices, setDevices] = useState<PkiDevice[] | null>(null);
  const [listErr, setListErr] = useState<unknown>(null);
  const loadList = async () => {
    setListErr(null);
    try {
      const res = await listDevices(statusFilter || undefined);
      setDevices(res.devices);
    } catch (e) {
      if (isNotConfigured(e)) setNotConfigured(true);
      else setListErr(e);
    }
  };

  if (notConfigured) {
    return (
      <ComingSoon
        title="憑證 / PKI"
        detail="device-CA 簽發服務尚未配置於目前環境（RA_DEVCA_URL/API_KEY 未設或 device-CA 不可達）。配置後本頁即啟用憑證查詢、CA chain 下載與裝置清單。"
      />
    );
  }

  return (
    <div className="pki-page">
      <header className="page-header">
        <div>
          <h1 className="page-title">憑證 / PKI</h1>
          <p className="page-subtitle">device-CA 簽發機（封閉 VM）憑證檢視。簽發不經本頁，見下方說明。</p>
        </div>
      </header>

      {/* 兩欄動作區:左＝單台簽發/查詢主操作;右＝CA chain + 安全設計常駐卡 */}
      <div className="adm-detail-grid cols-2">
        {/* 左欄:hero 卡 — 本頁主操作 */}
        <div className="card card--primary">
          <div className="adm-actionbar">
            <div className="adm-actionbar-head">
              <h2 className="adm-card-title">單台憑證查詢</h2>
            </div>

            <div className="form-row">
              <label htmlFor="pki-did">裝置 ID</label>
              <input
                id="pki-did"
                value={did}
                onChange={(e) => setDid(e.target.value)}
                placeholder="dev-000123（不透明字串）"
                autoComplete="off"
                spellCheck={false}
              />
            </div>

            <div className="adm-actionbar-btns">
              <button className="btn-primary" onClick={onLookup} disabled={busy} data-testid="pki-lookup-btn">
                查詢憑證
              </button>
            </div>
            {/* 結果區:未執行 / 處理中 / 錯誤 / 有結果 四態,一律用共用 state-block */}
            {busy ? (
              <LoadingState label="處理中…" />
            ) : error != null ? (
              <ErrorState error={error} />
            ) : result ? (
              <div className="result">
                <div className="result-head">
                  <span className={`pill pill-${result.status === 'active' ? 'good' : 'neutral'}`}>
                    {result.status === 'active' ? '使用中' : (result.status ?? '—')}
                  </span>
                  <span className="mono">{result.device_id}</span>
                </div>
                {/* device-ca `GET /v1/devices/{did}` 的 metadata 形狀 —— 沒有 cert PEM,
                    也沒有 CN/演算法。查詢面從來就取不到憑證本體(那要 bundle 端點,
                    而 bundle 含私鑰、平台的 key 也不該有那個 scope)。 */}
                <dl className="adm-kv">
                  <dt>憑證 ID</dt>
                  <dd className="mono">{result.cert_id ?? '—'}</dd>
                  <dt>序號</dt>
                  <dd className="mono small">{result.serial ?? '—'}</dd>
                  <dt>SHA-256</dt>
                  <dd className="mono small">{result.fingerprint ?? '—'}</dd>
                  <dt>CA 版本</dt>
                  <dd className="mono">{result.ca_version ?? '—'}</dd>
                  <dt>有效期</dt>
                  <dd>
                    {formatRfc3339(result.not_before)} → {formatRfc3339(result.not_after)}
                  </dd>
                </dl>
              </div>
            ) : (
              // 待操作提示(非「查無資料」)→ 用 inline 虛線 hint,保留 EmptyState 給真正空結果
              <div className="empty-state empty-state--inline">
                <div className="empty-state-detail">輸入裝置 ID 後，點擊「查詢憑證」。</div>
              </div>
            )}

          </div>
        </div>

        {/* 右欄:CA chain 精簡卡 + 常駐安全設計卡 */}
        <div className="pki-side-col">
          <div className="card">
            <h2 className="adm-card-title">CA 信任鏈下載</h2>
            <p className="muted">安裝信任錨 / 驗 device leaf 用（簽發 CA + 根 CA）。</p>
            <div className="btn-row">
              <button className="btn-ghost" onClick={() => void loadChain()}>
                載入 CA chain
              </button>
              {chain && (
                <button className="btn-ghost" onClick={() => download('ca-chain.pem', chain)}>
                  下載 ca-chain.pem
                </button>
              )}
            </div>
            {chainErr != null ? (
              <ErrorState error={chainErr} />
            ) : chain ? (
              <div className="pem-block">
                <div className="pem-head">
                  <span className="pem-badge">🔒 唯讀・公開憑證</span>
                </div>
                <pre className="pem">{chain}</pre>
              </div>
            ) : (
              <div className="empty-state empty-state--inline">
                <div className="empty-state-detail">點擊「載入 CA chain」查看目前信任錨鏈。</div>
              </div>
            )}
          </div>

          {/* 常駐安全設計卡:不需互動即可見,承接原頁首的信任訊息 */}
          <div className="card card--muted pki-security-card">
            <div className="pki-security-icon">🔒</div>
            <h2 className="adm-card-title">安全設計</h2>
            <p>
              <strong>私鑰永不離開簽發機。</strong>本頁經 restful-api 代理唯讀查詢封閉 VM 內的
              device-CA;代理拒絕轉送任何金鑰材料,主控台與瀏覽器全程只看得到公開 metadata。
            </p>
            <p>
              <strong>簽發不在這裡。</strong>首簽走 device-CA 的批量作業,RMA 換機走重簽,兩者都在
              封閉簽發機上執行(2026-08-02 業主裁決,restful-api#140)—— 對齊 ADR-019。
            </p>
            <p className="text-tertiary">
              若本頁顯示「尚未配置」,代表目前環境尚未接上 device-CA 服務,而非權限問題。
            </p>
            <dl className="pki-security-facts">
              <div>
                <dt>代理路徑</dt>
                <dd className="mono">/v3/admin/pki/*</dd>
              </div>
              <div>
                <dt>簽發機</dt>
                <dd>device-CA(封閉 VM)</dd>
              </div>
              <div>
                <dt>本頁權限</dt>
                <dd>唯讀(不含簽發)</dd>
              </div>
              <div>
                <dt>私鑰位置</dt>
                <dd>永不離開簽發機</dd>
              </div>
            </dl>
          </div>
        </div>
      </div>

      {/* 裝置 / 憑證清單:全寬,置於兩欄區塊下方 */}
      <section className="card pki-list-card">
        <h2 className="adm-card-title">裝置 / 憑證清單</h2>
        <div className="btn-row">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">全部狀態</option>
            <option value="active">使用中</option>
            <option value="deleted">已刪除</option>
          </select>
          <button className="btn-ghost" onClick={() => void loadList()}>
            載入清單
          </button>
        </div>
        {listErr != null ? (
          <ErrorState error={listErr} />
        ) : devices == null ? (
          <div className="empty-state empty-state--inline">
            <div className="empty-state-detail">點擊「載入清單」查看目前已簽發裝置與憑證狀態。</div>
          </div>
        ) : devices.length === 0 ? (
          <EmptyState icon="🔐" title="沒有符合篩選條件的裝置 / 憑證" />
        ) : (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>裝置 ID</th>
                  <th>狀態</th>
                  <th>到期</th>
                </tr>
              </thead>
              <tbody>
                {devices.map((d) => (
                  <tr key={d.device_id}>
                    <td className="mono">{d.device_id}</td>
                    <td>
                      <span className={`pill pill-dot pill-${d.status === 'active' ? 'good' : 'neutral'}`}>
                        {d.status === 'active' ? '使用中' : d.status === 'deleted' ? '已刪除' : (d.status ?? '—')}
                      </span>
                    </td>
                    <td>{d.not_after ? formatTimestampUs(d.not_after) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

    </div>
  );
}
